<template>
  <ClientOnly>
    <!-- Fully hidden until revealed by clicking the top-right "ID" 5x in 10s. -->
    <template v-if="!hidden">
    <!-- Collapsed: small floating toggle pill -->
    <button
      v-if="!isOpen"
      type="button"
      class="fixed bottom-20 right-4 z-[99999] flex items-center gap-2 rounded-full bg-sky-800/95 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-lg ring-1 ring-sky-400/40 backdrop-blur transition hover:bg-sky-700"
      title="Open Dev Event Override"
      @click="setOpen(true)"
    >
      <span class="inline-block h-2 w-2 rounded-full bg-sky-300"></span>
      ADMIN ONLY
    </button>

    <!-- Expanded: override panel -->
    <div
      v-else
      class="fixed bottom-4 right-4 z-[99999] w-[calc(100vw-2rem)] max-w-xs overflow-hidden rounded-2xl bg-sky-950/95 text-white shadow-2xl ring-1 ring-sky-400/40 backdrop-blur sm:w-80"
    >
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <span class="text-xs font-bold uppercase tracking-widest text-sky-200">
          Dev · Event Override
        </span>
        <button
          type="button"
          class="rounded-md p-1 text-sky-200 transition hover:bg-white/10 hover:text-white"
          title="Close"
          @click="setOpen(false)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Body -->
      <div class="space-y-3 px-4 py-4">
        <!-- Currently viewing -->
        <div class="flex items-center justify-between rounded-lg bg-black/30 px-3 py-2">
          <span class="text-xs text-sky-200">Viewing</span>
          <span class="max-w-[60%] truncate text-sm font-semibold" :title="currentEventId || '—'">
            {{ currentEventId || '—' }}
          </span>
        </div>

        <!-- Input + Switch -->
        <form class="flex items-stretch gap-2" @submit.prevent="onSwitch">
          <input
            v-model.trim="inputEventId"
            @input="inputEventId = inputEventId.toUpperCase()"
            type="text"
            inputmode="text"
            autocomplete="off"
            spellcheck="false"
            :disabled="loading"
            placeholder="Event ID"
            class="min-w-0 flex-1 rounded-lg border border-sky-400/40 bg-black/40 px-3 py-2 text-sm uppercase text-white placeholder:normal-case placeholder-sky-300/50 outline-none focus:border-sky-300 focus:ring-1 focus:ring-sky-300 disabled:opacity-50"
          />
          <button
            type="submit"
            :disabled="loading || !canSwitch"
            class="shrink-0 rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {{ loading ? '…' : 'Switch' }}
          </button>
        </form>

        <!-- Back to original + Hide -->
        <div class="flex items-stretch gap-2">
          <button
            type="button"
            :disabled="loading || !originalEventId"
            class="min-w-0 flex-1 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm font-medium text-sky-100 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
            @click="onRestore"
          >
            Back to original ID
            <span v-if="originalEventId" class="text-sky-300/70">({{ originalEventId }})</span>
          </button>
          <button
            type="button"
            class="shrink-0 rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-600"
            title="Hide this overlay"
            @click="onHide"
          >
            Hide
          </button>
        </div>

        <!-- Feedback -->
        <p v-if="message" class="text-xs" :class="messageIsError ? 'text-red-300' : 'text-emerald-300'">
          {{ message }}
        </p>
      </div>
    </div>
    </template>
  </ClientOnly>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useEventStore } from "@/stores/event";
import { useUserStore } from "@/stores/user";

const OPEN_STORAGE_KEY = "dev.eventOverride.open";
// The original event ID is saved here on Switch, so your DB restore code can read it back.
const ORIGINAL_STORAGE_KEY = "originalEventID";

const eventStore = useEventStore();
const userStore = useUserStore();
const { hidden, hide, syncFromStorage } = useDevOverride();

const isOpen = ref(false);
const inputEventId = ref("");
const originalEventId = ref<string | null>(null);
const loading = ref(false);
const message = ref("");
const messageIsError = ref(false);

/** The event ID currently being viewed. */
const currentEventId = computed<string | null>(() => (eventStore.eventId as string) || null);

const canSwitch = computed(
  () => !!inputEventId.value && inputEventId.value !== currentEventId.value
);

async function updateUserEventId(newEventId: string): Promise<void> {
  // --- DB write goes here -------------------------------------------------
  // e.g. await userStore.updateUserEvent(userStore.user?.uid, { eventId: newEventId })
  //      await eventStore.fetchEventRealtime()
  // ------------------------------------------------------------------------

  // Local state update so the app reflects the override immediately:
  await userStore.devUserUpdateEventId('set', newEventId)
  window.location.reload()
  // eventStore.eventId = newEventId;
  // if (userStore.user) userStore.user.eventId = newEventId;
}

async function restoreUserEventId(originalId: string): Promise<void> {
  // Local state update:

  await userStore.devUserUpdateEventId('restore', originalId)
  window.location.reload()
  // eventStore.eventId = originalId;
  // if (userStore.user) userStore.user.eventId = originalId;
}

async function onSwitch() {
  if (!canSwitch.value || loading.value) return;
  let target = inputEventId.value;
  loading.value = true;
  message.value = "";
  try {
    // Remember the original the first time we override.
    if (!originalEventId.value && currentEventId.value) {
      setOriginalEventId(currentEventId.value);
    }

    const isDemoIds = ['chompoo', 'demo1', 'demo2', 'demo3'] //allow lowercase
    // Demo IDs are stored in lowercase — convert the uppercased input back.
    if (isDemoIds.includes(target.toLowerCase())) {
      target = target.toLowerCase();
    }

    await updateUserEventId(target);
    inputEventId.value = "";
    showMessage(`Switched to "${target}".`, false);
  } catch (err: any) {
    showMessage(err?.message || "Failed to switch event.", true);
  } finally {
    loading.value = false;
  }
}

async function onRestore() {
  if (!originalEventId.value || loading.value) return;
  const target = originalEventId.value;
  loading.value = true;
  message.value = "";
  try {
    await restoreUserEventId(target);
    clearOriginalEventId();
    showMessage(`Restored to "${target}".`, false);
  } catch (err: any) {
    showMessage(err?.message || "Failed to restore event.", true);
  } finally {
    loading.value = false;
  }
}

/** Close the panel and hide the whole overlay until revealed via the ID clicks. */
function onHide() {
  setOpen(false);
  hide();
}

/* -------------------------------------------------------------------------- */
/*  Local persistence helpers                                                  */
/* -------------------------------------------------------------------------- */

function setOpen(value: boolean) {
  isOpen.value = value;
  try {
    localStorage.setItem(OPEN_STORAGE_KEY, value ? "1" : "0");
  } catch {}
}

function setOriginalEventId(value: string) {
  originalEventId.value = value;
  try {
    localStorage.setItem(ORIGINAL_STORAGE_KEY, value);
  } catch {}
}

function clearOriginalEventId() {
  originalEventId.value = null;
  try {
    localStorage.removeItem(ORIGINAL_STORAGE_KEY);
  } catch {}
}

let messageTimer: ReturnType<typeof setTimeout> | null = null;
function showMessage(text: string, isError: boolean) {
  message.value = text;
  messageIsError.value = isError;
  if (messageTimer) clearTimeout(messageTimer);
  messageTimer = setTimeout(() => (message.value = ""), 4000);
}

onMounted(() => {
  syncFromStorage();
  try {
    const savedOpen = localStorage.getItem(OPEN_STORAGE_KEY);
    if (savedOpen !== null) isOpen.value = savedOpen === "1";

    const savedOriginal = localStorage.getItem(ORIGINAL_STORAGE_KEY);
    if (savedOriginal) originalEventId.value = savedOriginal;
  } catch {}
});
</script>
